'use client';

import { Button, Filter } from '@sprd/sprd-component-kit/v2';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { NestedCategoryFilter } from '@/types/assortment';
import { useStateHistory } from '@/hooks/useStateHistory';
import { FilterDrawer } from '@/components/Filters/FilterDrawer';
import { ActiveListFilter, ListFacetsAndCategories } from '@/components/Filters/listFilter';
import { useRouter } from '@/i18n/navigation';
import { FEATURE_SUSTAINABLE_ID } from '@/utility/constants';
import { getCategoriesById, getDepartmentById } from '@/utility/listPages';
import { Locale } from '@/utility/locale';
import { post } from '@/utility/requestHelpers/clientRequestHelpers';
import { getShowroomHrefArgs } from '@/utility/urls';

type ProductListFacetsAndCategories = Omit<ListFacetsAndCategories, 'prices' | 'brand'>;
type MPFilterDrawerProps = {
  shopId: string;
  locale: Locale;
  userSlug: string;
  userId: string;
  filter: ProductListFacetsAndCategories;
  activeFilter: Omit<ActiveListFilter<ProductListPageSort>, 'maxPrice' | 'searchTerm'>;
  categories: NestedCategoryFilter[];
};

export const ShowroomFilterDrawer = ({
  shopId,
  locale,
  userSlug,
  userId,
  filter: initialFilter,
  activeFilter: initialActiveFilter,
  categories,
}: MPFilterDrawerProps) => {
  const t = useTranslations();

  const router = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [activeFilter, setNextFilter, setBackFilter, hasChanged] =
    useStateHistory<ActiveListFilter>(initialActiveFilter);

  const { data: filter, isError } = useQuery({
    queryKey: ['showroom-filter', shopId, locale, userId, activeFilter],
    queryFn: async () => {
      const filter = activeFilter;
      const { facets } = await post<{ facets: ProductListFacetsAndCategories }>(
        '/internal/api/marketplaceFilterFacets',
        {
          shopId,
          locale,
          filter: {
            category: filter?.category,
            sizeGroups: filter?.size,
            appearanceGroups: filter?.appearance,
            labels: {
              FEATURE: filter?.sustainable ? [FEATURE_SUSTAINABLE_ID] : undefined,
              GENDER: filter?.department ? [filter?.department] : undefined,
              BRAND: filter?.brand,
              REFINEMENT: filter?.refinement ? [filter?.refinement] : undefined,
              MATERIALS: filter?.materials ? [filter?.materials] : undefined,
              FIT: filter?.fit,
              STYLE: filter?.style ? [filter?.style] : undefined,
              NECKLINE: filter?.neckline,
              SLEEVE: filter?.sleeve,
            },
            userId,
            embroidery: filter?.embroidery,
          },
          sort: filter?.sort as ProductListPageSort,
        }
      );

      if (!facets) {
        throw new Error('[MP Filter Drawer]: No facets returned. Invalid filter combination');
      }

      return { ...facets, categories };
    },
    enabled: hasChanged,
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  useEffect(() => {
    if (isError) setBackFilter();
  }, [isError, setBackFilter]);

  const applyFilter = useCallback(
    (newFilter: Partial<ProductListFacetsAndCategories>, newActiveFilter: ActiveListFilter) => {
      const { department: newDepartment, category: newCategory, ...searchParams } = newActiveFilter;

      const {
        appearance,
        brand,
        fit,
        materials,
        neckline,
        refinement,
        size,
        sleeve,
        style,
        sort,
        ...otherParams
      } = searchParams;

      const href = getShowroomHrefArgs({
        userSlug,
        departmentSlug: newDepartment
          ? getDepartmentById(newFilter.departments ?? [], newDepartment)?.slug
          : undefined,
        categorySlugs: newCategory
          ? getCategoriesById(newFilter.categories ?? [], newCategory).map(
              (category) => category.slug
            )
          : undefined,
        queryParams: {
          ...otherParams,
          sort: sort as ProductListPageSort,
          appearance: appearance,
          brand: brand,
          fit: fit,
          materials: materials ? [materials] : undefined,
          neckline: neckline,
          refinement: refinement ? [refinement] : undefined,
          size: size,
          sleeve: sleeve,
          style: style ? [style] : undefined,
        },
      });

      setOpen(false);
      router.push(href, { scroll: false });
    },
    [router, userSlug]
  );

  const {
    size: selectedSize,
    appearance: selectedAppearance,
    fit: selectedFit,
    sleeve: selectedSleeve,
    neckline: selectedNeckline,
    embroidery: embroiderySelected,
    department: selectedDepartment,
  } = initialActiveFilter ?? {};

  const selectedFiltersCount = [
    selectedSize,
    selectedAppearance,
    selectedFit,
    selectedNeckline,
    selectedSleeve,
    embroiderySelected,
    selectedDepartment,
  ].filter(Boolean).length;

  const currentFilter = filter ?? initialFilter;

  return (
    <>
      <Button
        className="border-none text-sm font-semibold"
        variant="invert"
        onClick={() => setOpen(true)}
        startIcon={<Filter className="size-6" />}
        endIcon={
          selectedFiltersCount && (
            <span className="inline-flex size-4 items-center justify-center bg-black text-[10px] text-white">
              {selectedFiltersCount}
            </span>
          )
        }
      >
        {t('filter.addFilter')}
      </Button>
      <FilterDrawer
        open={open}
        filter={{ ...currentFilter, brand: undefined }}
        activeFilter={activeFilter}
        onFilterUpdate={setNextFilter}
        onApplyFilter={applyFilter}
      />
    </>
  );
};
